import React, { useMemo, useRef, useState, useEffect } from "react";
import * as d3 from "d3";

const max_length_legend = 25;

// Helper to transform the input data into a flat array for D3
function transformData(input) {
  const key = Object.keys(input)[0];
  const table = input[key];
  // Sort months chronologically
  const months = Object.keys(table).sort((a, b) => {
    const parseMonth = (str) => {
      const [month, year] = str.split(" ");
      return new Date(`${month} 1, ${year}`);
    };
    return parseMonth(a) - parseMonth(b);
  });
  // Flatten to [{month, category, value} ...] and filter out 0/null
  const flat = [];
  months.forEach((month) => {
    Object.entries(table[month]).forEach(([cat, val]) => {
      if (val !== 0 && val !== null) {
        flat.push({ month, category: cat, value: val });
      }
    });
  });
  return { flat, months };
}

const COLORS = d3.schemeTableau10;

const RenderBarChart = ({ data, barWidth = 28, colorMap }) => {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 700, height: 400 });

  // Responsive resize observer
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width: Math.max(width, 300), height: Math.max(height, 300) });
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const width = dimensions.width;
  const height = dimensions.height;

  // Memoize transformed data
  const { flat, months } = useMemo(() => transformData(data), [data]);
  // Get all unique categories (for color), excluding 'Total (Issues)'
  const categories = Array.from(new Set(flat.map((d) => d.category))).filter(cat => cat !== "Total (Issues)");
  // Use colorMap if provided, otherwise fallback to d3 color scale
  const colorScale = (cat) => {
    if (colorMap && colorMap[cat]) return colorMap[cat];
    // fallback to d3 color scale
    const d3Scale = d3.scaleOrdinal().domain(categories).range(COLORS);
    return d3Scale(cat);
  };

  // Compute x/y scales
  const x0 = d3
    .scaleBand()
    .domain(months)
    .range([60, width - 20])
    .paddingInner(0.2);
  // For each month, get the bars in order, excluding 'Total (Issues)', and sort by value descending
  const barsByMonth = months.map((month) =>
    flat
      .filter((d) => d.month === month && d.category !== "Total (Issues)")
      .sort((a, b) => b.value - a.value)
  );
  // For each month, compute the x offset for each bar (side by side)
  const maxBars = d3.max(barsByMonth, (bars) => bars.length) || 1;
  const barSpacing = barWidth + 6;
  // Y scale
  const legendItemWidth = 250; // px per legend item
  const legendHeight = (Math.floor(categories.length * legendItemWidth / (width - 80)) + 1) * 26 + 10; // estimate legend height
  const y = d3
    .scaleLinear()
    .domain([0, d3.max(flat, (d) => d.value) || 1])
    .nice()
    .range([height - 40, legendHeight + 30]);

  // For axis ticks
  const yTicks = y.ticks(5);

  // Calculate totals for each category, excluding 'Total (Issues)'
  const categoryTotals = useMemo(() => {
    const totals = {};
    flat.forEach(({ category, value }) => {
      if (category !== "Total (Issues)") {
        totals[category] = (totals[category] || 0) + value;
      }
    });
    return totals;
  }, [flat]);

  // Legend layout: top, side by side, wrap if too long
  const legendXStart = 60;
  const legendY = 10;
  const legendMaxWidth = width - 20;
  let legendX = legendXStart;
  let legendRow = 0;

  // Sort categories for legend in descending order of total value
  const sortedCategories = [...categories].sort((a, b) => (categoryTotals[b] || 0) - (categoryTotals[a] || 0));

  const legendElements = [];
  sortedCategories.forEach((cat, i) => {
    if (legendX + legendItemWidth > legendMaxWidth) {
      legendRow++;
      legendX = legendXStart;
    }
    const legendLabel = `${cat} | ${categoryTotals[cat]}`;
    const displayLabel = legendLabel.length > max_length_legend ? legendLabel.slice(0, max_length_legend) + '…' : legendLabel;
    legendElements.push(
      <g key={cat} transform={`translate(${legendX},${legendY + legendRow * 26})`}>
        <rect width={16} height={16} fill={colorScale(cat)} rx={2} />
        <text x={22} y={13} fontSize={13} fill="#333">
          {displayLabel}
          <title>{legendLabel}</title>
        </text>
      </g>
    );
    legendX += legendItemWidth;
  });

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
      <svg width={width} height={height}>
        {/* Legend on top, side by side, wraps if needed */}
        <g>{legendElements}</g>
        {/* Y Axis */}
        <g>
          <line x1={60} y1={y.range()[0]} x2={60} y2={y.range()[1]} stroke="#888" />
          {yTicks.map((tick) => (
            <g key={tick}>
              <line x1={55} x2={60} y1={y(tick)} y2={y(tick)} stroke="#888" />
              <text x={50} y={y(tick) + 4} fontSize={11} textAnchor="end" fill="#444">{tick}</text>
            </g>
          ))}
          {/* Y Axis Label */}
          <text
            x={20}
            y={(y.range()[0] + y.range()[1]) / 2}
            textAnchor="middle"
            fontSize={14}
            fill="#333"
            fontWeight="bold"
            transform={`rotate(-90 20 ${(y.range()[0] + y.range()[1]) / 2})`}
          >
            ISSUES
          </text>
        </g>
        {/* X Axis */}
        <g>
          <line x1={60} y1={y.range()[0]} x2={width - 20} y2={y.range()[0]} stroke="#888" />
          {months.map((month) => (
            <text
              key={month}
              x={x0(month) + ((barsByMonth.find((b) => b[0]?.month === month)?.length || 1) * barSpacing) / 2 - barSpacing / 2}
              y={y.range()[0] + 18}
              fontSize={12}
              textAnchor="middle"
              fill="#444"
            >
              {month}
            </text>
          ))}
          {/* X Axis Label */}
          <text
            x={(60 + width - 20) / 2}
            y={height - 5}
            textAnchor="middle"
            fontSize={14}
            fill="#333"
            fontWeight="bold"
          >
            DUE DATE
          </text>
        </g>
        {/* Bars */}
        {barsByMonth.map((bars, i) =>
          bars.map((d, j) => (
            <g key={d.category + d.month}>
              <rect
                x={x0(d.month) + j * barSpacing}
                y={y(d.value)}
                width={barWidth}
                height={y.range()[0] - y(d.value)}
                fill={colorScale(d.category)}
                rx={3}
              >
                <title>{`${d.category}: ${d.value}`}</title>
              </rect>
              <text
                x={x0(d.month) + j * barSpacing + barWidth / 2}
                y={y(d.value) - 6}
                fontSize={11}
                textAnchor="middle"
                fill="#222"
              >
                {d.value}
              </text>
            </g>
          )))
        }
      </svg>
    </div>
  );
};

export default RenderBarChart;
